import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiUsers, FiActivity, FiTrendingUp, FiPlus, FiArrowRight, FiClock } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { results as resultsApi, exams as examsApi } from '../../api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <div className="stat-card">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={22} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-black text-gray-900 mt-0.5">{value ?? <span className="w-16 h-6 bg-gray-100 rounded animate-pulse inline-block" />}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {trend && <div className={`text-xs font-bold px-2 py-1 rounded-full ${trend > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
      {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
    </div>}
  </div>
);

const PIE_COLORS = ['#1e3a5f', '#3b82f6', '#10b981', '#f59e0b'];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentExams, setRecentExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([resultsApi.getStats(), examsApi.getAll({ limit: 5 })])
      .then(([statsRes, examsRes]) => {
        setStats(statsRes.data.data);
        setRecentExams(examsRes.data.data || []);
      })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const barData = stats?.recentResults?.slice(0, 8).map(r => ({
    name: r.first_name,
    score: Math.round(parseFloat(r.percentage)),
  })) || [];

  const pieData = [
    { name: 'Pass', value: stats?.recentResults?.filter(r => r.is_passed).length || 0 },
    { name: 'Fail', value: stats?.recentResults?.filter(r => !r.is_passed).length || 0 },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">Overview of your assessment platform</p>
        </div>
        <Link to="/admin/exams/create" className="btn-primary">
          <FiPlus size={16} /> New Exam
        </Link>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={FiFileText} label="Total Exams" value={stats?.totalExams} sub="All statuses" color="bg-navy" />
        <StatCard icon={FiUsers} label="Total Candidates" value={stats?.totalCandidates} sub="Evaluated" color="bg-blue-500" />
        <StatCard icon={FiActivity} label="Live Now" value={stats?.liveParticipants} sub="Taking exam" color="bg-emerald-500" />
        <StatCard icon={FiTrendingUp} label="Avg Score" value={stats?.averageScore ? `${stats.averageScore}%` : null} sub="All exams" color="bg-amber-500" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bar chart */}
        <div className="card p-5 lg:col-span-2">
          <h3 className="font-bold text-gray-900 mb-4">Recent Candidate Scores</h3>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${v}%`, 'Score']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Bar dataKey="score" fill="#1e3a5f" radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No data yet</div>
          )}
        </div>

        {/* Pie chart */}
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-4">Pass / Fail Rate</h3>
          {(pieData[0].value + pieData[1].value) > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={3} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={i === 0 ? '#10b981' : '#ef4444'} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [v, 'Candidates']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-5 mt-2">
                {pieData.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 rounded-full" style={{ background: i === 0 ? '#10b981' : '#ef4444' }} />
                    <span className="text-gray-600">{d.name}: <strong>{d.value}</strong></span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-400 text-sm">No results yet</div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent exams */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Recent Exams</h3>
            <Link to="/admin/exams" className="text-sm text-navy font-semibold hover:underline flex items-center gap-1">
              View all <FiArrowRight size={13} />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />)}</div>
          ) : recentExams.length === 0 ? (
            <div className="text-center py-8">
              <FiFileText size={32} className="text-gray-200 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No exams yet</p>
              <Link to="/admin/exams/create" className="text-navy text-sm font-semibold hover:underline mt-1 inline-block">Create your first exam →</Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentExams.map(exam => (
                <Link key={exam.id} to={`/admin/exams/${exam.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                  <div className="w-9 h-9 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FiFileText size={16} className="text-navy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-navy">{exam.title}</p>
                    <p className="text-xs text-gray-400">{exam.question_count || 0} questions · {exam.duration_minutes} min</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${
                    exam.status === 'published' ? 'bg-emerald-50 text-emerald-700' :
                    exam.status === 'active' ? 'bg-blue-50 text-blue-700' :
                    exam.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                    'bg-amber-50 text-amber-700'
                  }`}>{exam.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent results */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Recent Results</h3>
            <Link to="/admin/results" className="text-sm text-navy font-semibold hover:underline flex items-center gap-1">
              View all <FiArrowRight size={13} />
            </Link>
          </div>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />)}</div>
          ) : (stats?.recentResults || []).length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No results yet</div>
          ) : (
            <div className="space-y-2">
              {(stats?.recentResults || []).slice(0, 5).map(r => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50">
                  <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-navy text-xs font-bold">{r.first_name?.[0]}{r.last_name?.[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r.first_name} {r.last_name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1"><FiClock size={10} /> {formatDistanceToNow(new Date(r.evaluated_at), { addSuffix: true })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">{parseFloat(r.percentage).toFixed(1)}%</p>
                    <span className={r.is_passed ? 'badge-pass' : 'badge-fail'}>{r.is_passed ? 'Pass' : 'Fail'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
